import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import cn from 'classnames'
import _ from 'lodash'
import moment from 'moment'
import Popover from 'antd/lib/popover'
import { Calendar } from 'antd'
import * as dateMath from 'date-arithmetic'
import * as instructorActions from '../../_actions/roster.actions'
import { viewConstants } from '../../_constants/view.constants'
import { navigate } from './constants'
import 'react-day-picker/lib/style.css'
import './css/style.css'

/**
 * PreviewToolbar — single toolbar for both month and week views.
 *
 * Props (from react-big-calendar):
 *   view, label, date, onNavigate, onView, localizer
 *
 * Extra props (from parent):
 *   currentView       'month' | 'week'   — the app-level view (not rbc internal)
 *   onViewChange      fn(view)            — called when user picks Week/Month
 *   showFilters       bool
 *   onToggleFilters   fn()
 *   hasActiveFilters  bool
 */
class PreviewToolbar extends React.Component {
    state = {
        calendarVisible: false,
        viewDropdownOpen: false,
        current: moment(this.props.date || new Date()),
    }

    componentDidMount() {
        document.addEventListener('mousedown', this._handleOutsideClick)
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._handleOutsideClick)
    }

    _handleOutsideClick = (e) => {
        if (this._viewDropdownEl && !this._viewDropdownEl.contains(e.target)) {
            this.setState({ viewDropdownOpen: false })
        }
    }

    hasActiveFilters = () => {
        const { selectedBranches, selectedDepartments, hasActiveDateRangeFilter } = this.props
        return (
            !_.isEmpty(this.props.selectedEmployees) ||
            !_.isEmpty(selectedBranches) ||
            !_.isEmpty(selectedDepartments) ||
            hasActiveDateRangeFilter
        )
    }

    // ── View dropdown ──────────────────────────────────────────────────────────
    toggleViewDropdown = () => this.setState(s => ({ viewDropdownOpen: !s.viewDropdownOpen }))

    selectView = (v) => {
        this.setState({ viewDropdownOpen: false })
        if (this.props.onViewChange) this.props.onViewChange(v)
    }

    // ── Calendar popover (date picker) ─────────────────────────────────────────
    handleCalendarVisibleChange = (visible) => this.setState({ calendarVisible: visible })

    onSelectDate = (date) => {
        const { currentView } = this.props
        this.setState({ calendarVisible: false, current: date })

        const range = currentView === 'month' ? viewConstants.MONTH : viewConstants.WEEK
        const options = { range, time: date.toDate() }

        if (this.hasActiveFilters()) {
            const { selectedEmployees, displayEmployees } = this.props
            if (!_.isEmpty(selectedEmployees)) {
                options.employees = selectedEmployees
            } else {
                options.employees = (displayEmployees || []).map(e => e.EmployeeAutoID)
            }
        }

        this.props.actions.rosterActions.updateCurrentDate({ currentDate: date.toDate() })

        if (currentView === 'month') {
            this.props.actions.rosterActions.fetchUserETime(options)
        } else {
            this.props.actions.rosterActions.fetchUpdateWorkETime(options)
        }

        if (this.props.onNavigate) {
            this.props.onNavigate(navigate.DATE, date.toDate())
        }
    }

    // ── Prev / Next navigation ─────────────────────────────────────────────────
    navigatePrev = () => this._doNavigate(navigate.PREVIOUS)
    navigateNext = () => this._doNavigate(navigate.NEXT)

    _doNavigate = (action) => {
        const { currentView, date } = this.props
        const unit = currentView === 'month' ? 'month' : 'week'
        const range = currentView === 'month' ? viewConstants.MONTH : viewConstants.WEEK

        const newDate = dateMath.add(date, action === navigate.PREVIOUS ? -1 : 1, unit)
        const newCurrent = dateMath.add(this.state.current.toDate(), action === navigate.PREVIOUS ? -1 : 1, unit)

        this.setState({ current: moment(newCurrent) })
        this.props.actions.rosterActions.updateCurrentDate({ currentDate: newDate })

        const options = { range, time: newDate }
        const { selectedEmployees, displayEmployees } = this.props

        if (this.hasActiveFilters()) {
            if (!_.isEmpty(selectedEmployees)) {
                options.employees = selectedEmployees
            } else {
                options.employees = (displayEmployees || []).map(e => e.EmployeeAutoID)
            }
        }

        if (currentView === 'month') {
            this.props.actions.rosterActions.fetchUserETime(options)
        } else {
            this.props.actions.rosterActions.fetchUpdateWorkETime(options)
        }

        if (this.props.onNavigate) this.props.onNavigate(action, date)
    }

    // ── Refresh current period ─────────────────────────────────────────────────
    refresh = () => {
        const { currentView, selectedEmployees, displayEmployees } = this.props
        const range = currentView === 'month' ? viewConstants.MONTH : viewConstants.WEEK
        const options = { range, time: this.state.current.toDate() }

        if (this.hasActiveFilters()) {
            if (!_.isEmpty(selectedEmployees)) {
                options.employees = selectedEmployees
            } else {
                options.employees = (displayEmployees || []).map(e => e.EmployeeAutoID)
            }
        }

        if (currentView === 'month') {
            this.props.actions.rosterActions.fetchUserETime(options)
        } else {
            this.props.actions.rosterActions.fetchUpdateWorkETime(options)
        }
    }

    // ── Label parsing ──────────────────────────────────────────────────────────
    getDisplayLabel = () => {
        const { label, currentView } = this.props
        if (!label) return ''

        if (currentView === 'month') {
            // "May 2026" → show just "May"
            const parts = label.split(' ')
            return parts[0] || label
        } else {
            // "May 11 – 17, 2026" → strip year
            const commaIdx = label.lastIndexOf(',')
            return commaIdx !== -1 ? label.substring(0, commaIdx).trim() : label
        }
    }

    render() {
        const { currentView, showFilters, onToggleFilters, hasActiveFilters } = this.props
        const { calendarVisible, viewDropdownOpen } = this.state

        const viewIcon = currentView === 'month' ? 'pi pi-calendar' : 'pi pi-table'
        const viewLabel = currentView === 'month' ? 'Month' : 'Week'
        const displayLabel = this.getDisplayLabel()

        const calendarPopover = (
            <div className="popover-calendar">
                <Calendar fullscreen={false} onSelect={this.onSelectDate} />
            </div>
        )

        return (
            <div className="rbc-toolbar custom-toolbar">
                <div className="rbc-toolbar-row">

                    {/* 1. View dropdown */}
                    <div
                        className="view-dropdown-wrapper"
                        ref={el => { this._viewDropdownEl = el }}
                    >
                        <button
                            type="button"
                            className="view-dropdown-btn"
                            onClick={this.toggleViewDropdown}
                        >
                            <i className={viewIcon} />
                            <span>{viewLabel}</span>
                            <i className="pi pi-chevron-down view-dropdown-chevron" />
                        </button>

                        {viewDropdownOpen && (
                            <div className="view-dropdown-menu">
                                <button
                                    type="button"
                                    className={cn('view-dropdown-item', {
                                        'view-dropdown-item--active': currentView === 'week',
                                    })}
                                    onClick={() => this.selectView('week')}
                                >
                                    <i className="pi pi-table" />
                                    <span>Week</span>
                                </button>
                                <button
                                    type="button"
                                    className={cn('view-dropdown-item', {
                                        'view-dropdown-item--active': currentView === 'month',
                                    })}
                                    onClick={() => this.selectView('month')}
                                >
                                    <i className="pi pi-calendar" />
                                    <span>Month</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* 2. Filter icon button */}
                    <button
                        type="button"
                        className={cn('toolbar-filter-btn', {
                            'toolbar-filter-btn--active': showFilters || hasActiveFilters,
                        })}
                        onClick={onToggleFilters}
                        title="Toggle filters"
                    >
                        <i className="pi pi-filter" />
                        {hasActiveFilters && <span className="filter-dot" />}
                    </button>

                    {/* 3. Navigation: < | label | > | refresh */}
                    <span className="date-navigation-group date-nav-week">
                        <button
                            type="button"
                            className="nav-week-btn nav-week-prev"
                            onClick={this.navigatePrev}
                        >
                            <i className="fas fa-angle-left" />
                        </button>

                        <Popover
                            content={calendarPopover}
                            placement="bottom"
                            trigger="click"
                            open={calendarVisible}
                            onOpenChange={this.handleCalendarVisibleChange}
                        >
                            <div className="nav-week-label">{displayLabel}</div>
                        </Popover>

                        <button
                            type="button"
                            className="nav-week-btn nav-week-next"
                            onClick={this.navigateNext}
                        >
                            <i className="fas fa-angle-right" />
                        </button>

                        <button
                            type="button"
                            className="nav-week-btn nav-week-today"
                            onClick={this.refresh}
                            title="Refresh"
                        >
                            <i className="pi pi-refresh" />
                        </button>
                    </span>

                </div>

                {/* Filter panel slot */}
                <div className={cn('filter-panel-horizontal', { open: showFilters })}>
                    {this.props.children}
                </div>
            </div>
        )
    }
}

function mapStateToProps(state) {
    return {
        displayEmployees: state.roster.displayEmployees,
        selectedEmployees: state.roster.selectedEmployees,
        selectedBranches: state.roster.selectedBranches,
        selectedDepartments: state.roster.selectedDepartments,
        hasActiveDateRangeFilter: state.roster.hasActiveDateRangeFilter,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: {
            rosterActions: bindActionCreators(instructorActions, dispatch),
        },
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PreviewToolbar)
